import React, { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_FABRIC_PALETTE,
  UPDATE_FABRIC_PALETTE,
  GET_FABRIC_PALETTES,
  GET_FABRIC_PALETTE,
  GET_FABRIC_GROUP_FOR_PALETTE,
} from '../fabricPaletteQueries'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  PaletteGroupColors,
  GroupColorState,
  countLabel,
  sortFabrics,
} from './PaletteGroupColors'
import { Trash2 } from 'lucide-react'
import { FabricGroupPicker } from './FabricGroupPicker'
import { toast } from 'sonner'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface FabricPaletteFormProps {
  data?: any
  onClose?: () => void
  // ContentDrawer passes this one (not onClose)
  onRequestClose?: () => void
}

interface EditableNameInputProps {
  groupId: number
  initialValue: string
  onNameChange: (groupId: number, value: string) => void
}

const EditableNameInput: React.FC<EditableNameInputProps> = ({
  groupId,
  initialValue,
  onNameChange,
}) => {
  const [value, setValue] = useState(initialValue)

  // Update local state when initialValue changes (e.g., when data loads)
  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    onNameChange(groupId, newValue)
  }

  return (
    <Input
      value={value}
      onChange={handleChange}
      placeholder="Enter custom name"
      className="min-w-[200px]"
    />
  )
}

export default function FabricPaletteForm({
  data,
  onClose: onCloseProp,
  onRequestClose,
}: FabricPaletteFormProps) {
  const onClose = onCloseProp ?? onRequestClose
  const form = useForm({ defaultValues: { name: '', code: '' } })
  const client = useApolloClient()
  // The drawer's "Create New +" passes a click event (or nothing) as `data`,
  // so only a real row with a numeric id means edit mode.
  const paletteId: number | undefined =
    typeof data?.id === 'number' ? data.id : undefined
  const isEdit = paletteId !== undefined
  // Working copy of the palette's groups: loaded from the backend, then
  // added to / removed from locally. Sent in full on save.
  const [fabricGroups, setFabricGroups] = useState<any[]>([])
  const [addingGroup, setAddingGroup] = useState(false)
  const lastRemoveId = React.useRef<number | null>(null)
  const [removeTarget, setRemoveTarget] = useState<{
    id: number
    code: string
    name: string
  } | null>(null)
  const [openGroups, setOpenGroups] = useState<string[]>([])
  const [editedNames, setEditedNames] = useState<{ [key: number]: string }>({})

  const handleNameChange = useCallback((groupId: number, newName: string) => {
    setEditedNames(prev => ({
      ...prev,
      [groupId]: newName,
    }))
  }, [])

  const [colorStates, setColorStates] = useState<{
    [key: number]: GroupColorState
  }>({})

  const { data: paletteData } = useQuery(GET_FABRIC_PALETTE, {
    variables: {
      id: paletteId,
    },
    skip: !isEdit,
    onCompleted: data => {
      if (data?.findUniqueFabricPalette) {
        form.reset({
          name: data.findUniqueFabricPalette.name ?? '',
          code: data.findUniqueFabricPalette.code ?? '',
        })
        setFabricGroups(data.findUniqueFabricPalette.fabric_groups)

        const initialColors: { [key: number]: GroupColorState } = {}
        data.findUniqueFabricPalette.fabric_groups.forEach((group: any) => {
          const codes: string[] | null = Array.isArray(group.color_codes)
            ? group.color_codes
            : null
          const overrides: GroupColorState['overrides'] = {}
          if (Array.isArray(group.color_overrides)) {
            group.color_overrides.forEach((o: any) => {
              overrides[o.code] = {
                name: o.name ?? '',
                category: o.category ?? '',
                name_source: o.name_source === 'admin' ? 'admin' : 'pim',
                category_source:
                  o.category_source === 'admin' ? 'admin' : 'pim',
                loaded_name: o.name ?? '',
                loaded_category: o.category ?? '',
              }
            })
          }
          initialColors[group.fabric_group.id] = {
            all: codes === null,
            selected: codes ?? [],
            overrides,
            dirty: false,
          }
        })
        setColorStates(initialColors)

        // Initialize editedNames with existing values
        const initialNames: { [key: number]: string } = {}
        data.findUniqueFabricPalette.fabric_groups.forEach((group: any) => {
          if (group.name) {
            initialNames[group.fabric_group.id] = group.name
          }
        })
        setEditedNames(initialNames)
      }
    },
    fetchPolicy: 'network-only',
  })

  const [createFabricPalette] = useMutation(CREATE_FABRIC_PALETTE, {
    refetchQueries: ['GET_FABRIC_PALETTES'],
  })

  const handleAddGroup = async (groupId: number) => {
    if (fabricGroups.some(g => g.fabric_group.id === groupId)) return
    setAddingGroup(true)
    try {
      const res = await client.query({
        query: GET_FABRIC_GROUP_FOR_PALETTE,
        variables: { id: groupId },
        fetchPolicy: 'network-only',
      })
      const fg = res.data?.findUniqueFabricGroup
      if (!fg) throw new Error('Fabric group not found')
      setFabricGroups(prev =>
        prev.some(g => g.fabric_group.id === groupId)
          ? prev
          : [...prev, { id: `new-${groupId}`, name: '', fabric_group: fg }]
      )
      // New group = "All colours", not dirty => no colour fields are sent.
      setColorStates(prev => ({
        ...prev,
        [groupId]: { all: true, selected: [], overrides: {}, dirty: false },
      }))
    } catch (e) {
      console.error(e)
      toast.error('Failed to add fabric group')
    } finally {
      setAddingGroup(false)
    }
  }

  const handleRemoveGroup = (groupId: number) => {
    setOpenGroups(prev => prev.filter(v => v !== String(groupId)))
    setFabricGroups(prev => prev.filter(g => g.fabric_group.id !== groupId))
    setColorStates(prev => {
      const rest = { ...prev }
      delete rest[groupId]
      return rest
    })
    setEditedNames(prev => {
      const rest = { ...prev }
      delete rest[groupId]
      return rest
    })
  }

  const [updateFabricPalette] = useMutation(UPDATE_FABRIC_PALETTE, {
    onError: e => {
      toast.error('Failed to update palette')
    },
    onCompleted: e => {
      toast.success('Fabric group updated palette')
    },
    refetchQueries: ['GET_FABRIC_PALETTES'],
  })

  const onSubmit = async (values: any) => {
    try {
      // Merge form values with edited names
      const fabricGroupsWithNames: any[] = []
      for (const group of fabricGroups) {
        const gid = group.fabric_group.id
        const entry: any = {
          fabricGroupId: gid,
          name:
            editedNames[gid] !== undefined
              ? editedNames[gid]
              : group.name || '',
        }
        const cs = colorStates[gid]
        // Only send colour fields for edited groups: omitted = keep existing.
        if (cs?.dirty) {
          if (!cs.all && cs.selected.length === 0) {
            toast.error(
              `Group ${group.fabric_group.code}: select at least one colour or use "All colours"`
            )
            return
          }
          const known = new Set<string>(
            group.fabric_group.fabrics.map((f: any) => f.code)
          )
          const ordered = sortFabrics(group.fabric_group.fabrics)
            .map((f: any) => f.code as string)
            .filter(c => cs.selected.includes(c))
          // keep codes missing from the group (data lag) instead of dropping them
          const missing = cs.selected.filter(c => !known.has(c))
          entry.color_codes = cs.all ? null : [...ordered, ...missing]
          entry.color_overrides = Object.entries(cs.overrides)
            .map(([code, o]) => {
              const name = o.name.trim()
              const category = o.category.trim()
              const out: any = { code }
              // changed vs loaded -> admin; unchanged -> loaded source; cleared -> dropped
              if (name) {
                out.name = name
                out.name_source =
                  o.name !== o.loaded_name ? 'admin' : o.name_source
              }
              if (category) {
                out.category = category
                out.category_source =
                  o.category !== o.loaded_category ? 'admin' : o.category_source
              }
              return out
            })
            .filter(o => o.name || o.category)
        }
        fabricGroupsWithNames.push(entry)
      }

      const submitData = {
        name: values.name,
        code: values.code || null,
        fabric_groups_with_names: fabricGroupsWithNames,
      }

      if (isEdit) {
        await updateFabricPalette({
          variables: {
            where: { id: paletteId },
            ...submitData,
          },
        })
      } else {
        await createFabricPalette({
          variables: submitData,
        })
        toast.success('Fabric palette created')
      }
      onClose?.()
    } catch (error) {
      console.error('Error saving fabric palette:', error)
      if (!isEdit) toast.error('Failed to create palette')
    }
  }

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>{isEdit ? 'Palette' : 'New palette'}</SheetTitle>
        </div>
        <SheetDescription>
          {isEdit
            ? `Id: ${paletteId} | Updated: ${paletteData?.findUniqueFabricPalette?.updatedAt ?? ''}`
            : 'New palette'}
        </SheetDescription>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Name"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter palette name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter palette code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex items-center justify-between pt-3">
                <FormLabel className="text-left">Fabric groups</FormLabel>
                <FabricGroupPicker
                  selectedIds={fabricGroups.map(g => g.fabric_group.id)}
                  disabled={addingGroup || (isEdit && !paletteData)}
                  onPick={handleAddGroup}
                />
              </div>
              {fabricGroups.length === 0 && !(isEdit && !paletteData) ? (
                <p className="text-sm text-muted-foreground">
                  No fabric groups yet. Use "Add fabric group" to add one.
                </p>
              ) : null}
              {form.getValues('code') ? (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  Synced from PIM nightly: colour selection is overwritten on
                  each import; colour names edited here are kept.
                </div>
              ) : null}
              <TooltipProvider>
              <Accordion
                type="multiple"
                className="w-full"
                value={openGroups}
                onValueChange={setOpenGroups}
              >
                {fabricGroups.map((group: any) => {
                  const gid = group.fabric_group.id
                  const fabrics = group.fabric_group.fabrics ?? []
                  const cs = colorStates[gid]
                  const groupName =
                    group.fabric_group.fabric_group_profiles[0]?.name ||
                    group.name ||
                    group.fabric_group.code
                  return (
                    <AccordionItem key={gid} value={String(gid)}>
                      <AccordionPrimitive.Header className="flex items-center gap-2">
                        <AccordionPrimitive.Trigger className="flex items-center gap-3 py-4 text-sm font-medium text-left rounded-sm transition-colors hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                          <span className="font-mono text-sm">
                            {group.fabric_group.code}
                          </span>
                          <span>{groupName}</span>
                          {String(group.id).startsWith('new-') && (
                            <Badge>new</Badge>
                          )}
                          {cs && (
                            <Badge variant="secondary">
                              {countLabel(fabrics, cs)}
                            </Badge>
                          )}
                        </AccordionPrimitive.Trigger>
                        {openGroups.includes(String(gid)) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="Remove group"
                                data-remove-group={gid}
                                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                                onClick={() => {
                                  lastRemoveId.current = gid
                                  setRemoveTarget({
                                    id: gid,
                                    code: group.fabric_group.code,
                                    name: groupName,
                                  })
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove group</TooltipContent>
                          </Tooltip>
                        )}
                        {/* second trigger: fills the rest of the header and
                            carries the chevron, so the whole row toggles */}
                        <AccordionPrimitive.Trigger
                          aria-label="Toggle group"
                          className="flex flex-1 items-center justify-end self-stretch py-4 rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&[data-state=open]>svg]:rotate-180"
                        >
                          <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                        </AccordionPrimitive.Trigger>
                      </AccordionPrimitive.Header>
                      <AccordionContent className="space-y-4">
                        <div className="flex items-center gap-2">
                          <EditableNameInput
                            groupId={gid}
                            initialValue={
                              editedNames[gid] !== undefined
                                ? editedNames[gid]
                                : group.name || ''
                            }
                            onNameChange={handleNameChange}
                          />
                        </div>
                        {cs && (
                          <PaletteGroupColors
                            fabrics={fabrics}
                            manufacturerId={group.fabric_group.manufacturer?.id}
                            defaultCategories={(
                              (group.fabric_group.fabric_price_category ??
                                []) as { group_number: number }[]
                            ).map(c => String(c.group_number))}
                            state={cs}
                            onChange={next =>
                              setColorStates(prev => ({ ...prev, [gid]: next }))
                            }
                          />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  )
                })}
              </Accordion>
              </TooltipProvider>
              <AlertDialog
                open={removeTarget !== null}
                onOpenChange={o => {
                  if (!o) setRemoveTarget(null)
                }}
              >
                <AlertDialogContent
                  onCloseAutoFocus={e => {
                    // the opener (trash) may be gone after Remove; restore
                    // focus to it, else to the nearest group toggle
                    e.preventDefault()
                    const root = document.querySelector('.sheet-content')
                    const el =
                      root?.querySelector<HTMLElement>(
                        `[data-remove-group="${lastRemoveId.current}"]`
                      ) ??
                      root?.querySelector<HTMLElement>(
                        '[aria-label="Toggle group"]'
                      )
                    el?.focus()
                  }}
                >
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove fabric group?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {removeTarget?.code} {removeTarget?.name} and its colour
                      selection and names for this palette will be removed when
                      you save.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className={cn(buttonVariants({ variant: 'destructive' }))}
                      onClick={() => {
                        if (removeTarget) handleRemoveGroup(removeTarget.id)
                        setRemoveTarget(null)
                      }}
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  form.formState.isSubmitting || (isEdit && !paletteData)
                }
              >
                {isEdit ? 'Update' : 'Create'} Fabric Palette
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
